import Layer from '@arcgis/core/layers/Layer';
import type { ExternalLayer } from 'hooks/externalLayer/types';
import WebMap from '@arcgis/core/WebMap';
import { arcGisGetOrCreatePopupTemplate } from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/arcGisGetOrCreatePopupTemplate';
import {
  createPortalItem,
  createServiceUrlLayer,
  type ArcGisAuthOptions,
} from 'components/MapPage/CorporateViewTabs/LayersTab/arcgis-integration/externalLayerLoader';

type ExternalLayerSyncItem = Pick<
  ExternalLayer,
  | 'id'
  | 'arcgisItemId'
  | 'serviceUrl'
  | 'incident'
  | 'authType'
  | 'defaultVisible'
>;

/**
 * Web Maps contain geometries + label styling + popups. Feature Services
 * contain only geometries
 */
export async function syncExternalLayers(
  externalLayer: ExternalLayerSyncItem,
  options: ArcGisAuthOptions
) {
  // Load as Web Map first, matches AGOL exactly
  if (externalLayer.arcgisItemId && options) {
    try {
      const wm = new WebMap({
        portalItem: await createPortalItem({
          portalItemId: externalLayer.arcgisItemId,
          options,
        }),
      });

      await wm.load();
      await wm.loadAll();
      const wmLayers = wm.layers.toArray();
      await arcGisGetOrCreatePopupTemplate(
        wmLayers,
        externalLayer.incident?.name
      );

      return { layers: wmLayers, loadError: false };
    } catch {
      console.warn('Failed to load WebMap from PortalItem()', {
        externalLayer,
      });

      // Fallback if not a Web Map
      try {
        const l = await Layer.fromPortalItem({
          portalItem: await createPortalItem({
            portalItemId: externalLayer.arcgisItemId,
            options,
          }),
        });
        await l.load();
        await arcGisGetOrCreatePopupTemplate([l], externalLayer.incident?.name);

        return { layers: [l], loadError: false };
      } catch (e) {
        console.warn(
          'Failed to load layer fromPortalItem()',
          {
            externalLayer,
          },
          e
        );
      }
    }
  }

  // Fallback to service url
  if (externalLayer.serviceUrl) {
    try {
      const layer = await createServiceUrlLayer({
        serviceUrl: externalLayer.serviceUrl,
        options,
      });
      await arcGisGetOrCreatePopupTemplate(
        [layer],
        externalLayer.incident?.name
      );
      return { layers: [layer], loadError: false };
    } catch (e) {
      console.warn(
        'Failed to load layer fromArcGISServerUrl()',
        {
          externalLayer,
        },
        e
      );
    }
  }

  return { layers: [], loadError: true };
}
